import { InAppBrowser } from '@awesome-cordova-plugins/in-app-browser'
import { IonActionSheet } from '@ionic/react'
import dayjs from 'dayjs'
import isBetween from 'dayjs/plugin/isBetween'
import { close, link, stopwatch } from 'ionicons/icons'
import React, { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory, useParams } from 'react-router-dom'
import { logEvent } from '../../../analytics/utils/firebaseUtils'
import { AppContext } from '../../../contexts/AppContext'
import useUser from '../../../contexts/hooks/useUser'
import { ObjectContext } from '../../../contexts/objectContext/ObjectContext'
import { trpc } from '../../../modules/trpc'
import { getInterventionsFromInverter } from '../../../utils/getInterventionIdsWithoutDuplicates'
import PageWrapper from '../../../wrappers/pageWrapper/PageWrapper'
import { useChartTime } from '../../electricity/chartUtils/hooks/useChartTime'
import InterventionList from '../electricCar/components/Interventions'
import ProductionDoughnut from './components/ProductionDoughnut'
import SolarPanelChart from './components/SolarPanelChart'
import { useSolarPanel } from './hooks/useSolarPanel'

dayjs.extend(isBetween)

const SolarPanelDetail = () => {
	const utils = trpc.useContext()
	const history = useHistory()
	const { inverterId } = useParams<{ inverterId: string }>()

	const [chartData, setChartData] = useState<number[]>([])
	const [moneyData, setMoneyData] = useState<number>(0)
	const [menuOpen, setMenuOpen] = useState(false)
	const [interventions, setInterventions] = useState<string[]>([])

	const [currentProduction, setCurrentProduction] = useState(0)

	const { isNativePlatform } = useContext(AppContext)
	const { panelMaxPower } = useContext(ObjectContext)

	const { user } = useUser()
	const time = useChartTime('week', true)

	const { t } = useTranslation('pages_solarPanelDetail')

	const inverter = useSolarPanel(
		{ span: time.span, startTime: time.startTime, endTime: time.endTime },
		[inverterId]
	)

	useEffect(() => {
		if (!inverter?.inverter) return
		const interventions = getInterventionsFromInverter(inverter.inverter)
		setInterventions(interventions)
	}, [inverter?.inverter])

	useEffect(() => {
		const getInverterStats = () => {
			return inverter.inverterStatistics ?? []
		}

		const inverterStats = getInverterStats()

		const kwhData = inverterStats?.map((stat: any) => stat.value) ?? []
		const moneyData = inverter?.savings

		if (kwhData) setChartData(kwhData)
		setMoneyData(moneyData)

		setCurrentProduction(inverter?.inverter?.productionState?.productionRate ?? 0)
	}, [inverter?.isLoading, inverter?.inverterStatistics, time.span, time.startTime, time.endTime])

	const unlink = trpc.eNode.user.disconnect.vendorType.useMutation({
		onSuccess: () => {
			utils.eNode.inverter.invalidate()
			history.goBack()
		},
		onError: () => {
			console.error('Error delinking inverter')
		},
	})

	const baseUrl = isNativePlatform
		? 'https://vihreaenergia.fi/'
		: new URL('/devices', window.location.origin).href
	const redirectUri = new URL('/devices', baseUrl).toString()

	const { refetch: refetchRelinkInverter } = trpc.eNode.user.relink.useQuery(
		{ assetId: inverter?.inverter?.id ?? '', language: 'browser', redirectUri },
		{
			enabled: false,
			onSuccess: data => {
				if (!data?.linkUrl) return
				if (isNativePlatform) {
					const browser = InAppBrowser.create(data.linkUrl, '_blank', 'location=yes')
					browser.on('loadstart').subscribe((event: any) => {
						if (
							event.url.includes('vihreaenergia.fi') ||
							event.url.includes(window.location.origin)
						) {
							browser.close()
							utils.objects.getEnergyReport.invalidate()
							utils.eNode.inverter.invalidate()
							history.goBack()
						}
					})
				} else {
					window.open(data.linkUrl, '_blank')
				}
			},
			onError: error => {
				console.error('Error relinking inverter', error)
			},
		}
	)

	return (
		<>
			<PageWrapper
				page='solarPanelDetail'
				appBarOptions={{
					ellipsisFunction: () => setMenuOpen(true),
					navigationType: 'back',
				}}
			>
				<InterventionList interventionIds={interventions} />
				<div className='grid place-items-center mb-8'>
					<ProductionDoughnut
						data={[
							Math.min(currentProduction * 1000, panelMaxPower || 0),
							Math.max((panelMaxPower || 0) - currentProduction * 1000, 0),
						]}
						centerText={`${currentProduction * 1000} W`}
						centerSubText={t('production')}
					/>
				</div>

				<SolarPanelChart
					time={time}
					chartData={chartData}
					moneyData={moneyData}
					isLoading={inverter?.isLoading || false}
				/>
			</PageWrapper>

			<IonActionSheet
				isOpen={menuOpen}
				onDidDismiss={() => setMenuOpen(false)}
				buttons={[
					{
						text: t('unlink'),
						icon: stopwatch,
						handler: () => {
							logEvent('click-solar-panel-unlink')
							unlink.mutate({
								userId: user!.id,
								userHashedPid: user!.hashedPid,
								vendor: inverter?.inverter?.vendor ?? '',
								vendorType: 'inverter',
							})
						},
					},
					{
						text: t('peruuta', { ns: 'common' }),
						role: 'cancel',
						icon: close,
						handler: () => {
							logEvent('click-generic-cancel')
						},
					},
					{
						text: t('relink'),
						icon: link,
						handler: () => {
							logEvent('click-solar-panel-relink')
							if (!inverter?.inverter?.id) return
							refetchRelinkInverter()
						},
					},
				]}
			/>
		</>
	)
}

export default SolarPanelDetail
